import { useEffect, useMemo } from "react";
import { Button } from "@vds/buttons";
import { Box } from "@mui/material";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../styles/deviceStyle.css";
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import AlertsTable from "../../global/components/Tables/AlertsTableWithHOC";
import ErrorTemplate from "../../global/components/errorTemplate/ErrorTemplate";
import { TEXT } from "../../global/constants/errorTemplateConstants";
import { useDispatch, useSelector } from "react-redux";
import {
  getLocationByCamera,
  getROIDatabyRoiID,
} from "../../global/utility/Utils";
import { transformPolygonData } from "../../global/utility/roiUtils";
import { hasPermission } from "../../global/utility/permissionUtils";
import { showNotificationBanner, fetchRulesListRequest } from "../../store/actions";
import { usePaginationReset, setDetailNavSource } from "../../hooks/usePaginationReset";
import { PAGE_IDENTIFIERS } from "../../global/constants/tableConstants";
import { useWindowSize } from "../../global/hooks/viewportHooks";

const Alerts = () => {
  const roiList = useSelector((state) => state.deviceReducer.roiList) || [];
  const { cameraList } = useSelector((state) => state?.deviceReducer);
  const { rules, loading: rulesLoading, error: rulesError } = useSelector((state) => state.alertsListReducer);
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const { state } = useLocation();
  const deletedRule = state?.showNotification;
  const updatedRule = state?.showUpdate;
  const createdRule = state?.create;

  const isFromOwnDetailPage = usePaginationReset(PAGE_IDENTIFIERS.ALERTS);

  // Show the loader only when there's no cached data yet; once rules exist in
  // the store (persisted or previously fetched), background refreshes stay silent.
  const tableLoading = rulesLoading && rules?.length === 0;
  const showErrorTemplate = !!rulesError && rules?.length === 0;

  const handleRefresh = () => {
    dispatch(fetchRulesListRequest());
  };

  const ruleList = useMemo(() => {
    const updatedRuleList = rules?.map((rule) => {
      const cameraInfo = getLocationByCamera(rule?.cameraId, cameraList);
      const roiData = getROIDatabyRoiID(roiList, rule?.deviceid);
      const roiPolygon = transformPolygonData(roiData);

      return {
        ...rule,
        site: cameraInfo?.site,
        area: cameraInfo?.area,
        floor: cameraInfo?.floor,
        cameraName: cameraInfo?.cameraName,
        roiData: roiData,
        roiPolygon: roiPolygon,
        Location: cameraInfo?.site,
        siteId: cameraInfo?.siteId,
        areaId: cameraInfo?.areaId,
        floorId: cameraInfo?.floorId,
      };
    });
    return (
      updatedRuleList?.sort(
        (b, a) =>
          new Date(a.createdon).getTime() - new Date(b.createdon).getTime()
      ) || []
    );
  }, [rules, cameraList, roiList]);

  useEffect(() => {
    if (!isFromOwnDetailPage) {
      dispatch(fetchRulesListRequest());
    }
  }, [isFromOwnDetailPage]);

  const createRules = () => {
    if (!hasPermission("Alerts", "Create")) {
      showPermissionsBanner();
    } else {
      setDetailNavSource(PAGE_IDENTIFIERS.ALERTS);
      navigate("/createAlert");
    }
  };

  const CreateAlertButton = (
    <div style={{ alignContent: "end" }}>
      <Button
        size={width < 768 ? "small" : "large"}
        disabled={false}
        use="primary"
        data-testid="alerts_createAlertButton"
        onClick={() => {
          createRules();
        }}
      >
        Create Alert
      </Button>
    </div>
  );

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };
  if (createdRule && createdRule === true) {
    const notificationInfo = {
      type: "Alert created",
      priority: "success",
      data: {
        title: "",
        subTitle: "Alert created successfully",
      },
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  }
  if (deletedRule && deletedRule === true) {
    const notificationInfo = {
      type: "Alert deleted",
      priority: "success",
      data: {
        title: "",
        subTitle: "Alert deleted successfully",
      },
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  }
  if (updatedRule && updatedRule === true) {
    const notificationInfo = {
      type: "Alert deleted",
      priority: "success",
      data: {
        title: "",
        subTitle: "Alert updated successfully",
      },
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  }

  return (
    <>
      <Breadcrumbs surface="light">
        <BreadcrumbItem>
          <Link
            data-testid="rules_settingsLink"
            to="/settings"
            className="breadcrums-item_link"
          >
            Settings
          </Link>
        </BreadcrumbItem>
        <BreadcrumbItem>Alerts</BreadcrumbItem>
      </Breadcrumbs>
      <div className="rule-heading">Alerts</div>

      {!tableLoading && showErrorTemplate ? (
        <Box sx={{ height: "600px" }}>
          <ErrorTemplate
            title={TEXT.RULES_FETCH_ERROR}
            description={TEXT.RULES_FETCH_ERROR_DESC}
            showRefreshButton={true}
            onRefresh={handleRefresh}
            templateType="error"
          />
        </Box>
      ) : (
        <AlertsTable
          ruleList={ruleList}
          tableLoading={tableLoading}
          CreateAlertButton={CreateAlertButton}
        />
      )}
    </>
  );
};

export default Alerts;
